import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  DeviceResponseType as ChannelResponseType,
  DeviceTaskType as ChannelTaskType,
} from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import {
  BlindErrorCode,
  BlindRemoteButtonState,
  BlindStatusCode,
  ConfigurationStateEnum,
  IDeviceState as IChannelState,
} from 'lavva.exalushome/build/js/Services/Devices/IDeviceState';
import { BlindActionEnum } from 'lavva.exalushome/build/js/Services/Devices/Tasks/Tasks';
import { IconConnectionError, IconWarning } from '../../../../../../../assets/icons';
import { ControlWrapper, EmptyStateBox, Slider } from '../../../../../../../components';
import { BlindDetailsSimpleControlButtons } from '../../../../../../../components/channel-details-controls/blind-simple';
import { BlindDetailsControls } from '../../../../../../../components/channel-details-controls/blind/controls';
import LavvaDetailsWrapper from '../../../../../../../components/details-wrapper';
import { useBlindErrors } from '../../../../../modules/channel-list/hooks/use-blind-errors';
import { useSomethingWrong } from '../../../../../modules/channel-list/hooks/use-something-wrong';
import { canChannelDo } from '../../../../../utils';
import { useBlindControl } from '../../../hooks/use-blind-control';
import Favourites from './favourites';

type BlindDetailsControlssProps = {
  channel: IDeviceChannel;
  active: boolean;
};

export const BlindDetailsControlss: React.FC<BlindDetailsControlssProps> = ({ channel, active }) => {
  const { t: tc } = useTranslation('common');
  const { t: td } = useTranslation('channel-details');
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [targetValue, setTargetValue] = useState<number>(0);
  const { open, stop, close, handleAction, handleSliderEvent } = useBlindControl(channel);
  const { connectionIcon, calibrationNeed, channelOrientedError } = useSomethingWrong(channel, { detailsView: true });
  const { blindPositionState, blindErrors } = useBlindErrors(channel);

  const status = useMemo(() => {
    if (blindErrors.length) return BlindErrorCode[blindErrors[0]];
    if (blindPositionState?.GetBlindStatus) {
      const st = blindPositionState.GetBlindStatus();
      if (st === BlindStatusCode.UnableToMove || st === BlindStatusCode.MotorOverheatedPleaseWait) {
        return BlindStatusCode[st];
      }
    }

    return null;
  }, [blindPositionState, blindErrors]);

  const isViewSimple = useMemo(() => canChannelDo(channel, [ChannelTaskType.SetBlindPositionSimple]), [channel]);

  const remoteButtonState = useMemo(() => {
    return channel.States?.find(
      (state) => state.TypeAsEnum === ChannelResponseType.BlindRemoteButtonState,
    ) as IChannelState<BlindRemoteButtonState>;
  }, [channel.States]);

  useEffect(() => {
    if (typeof blindPositionState?.Data?.Position === 'number') {
      if (blindPositionState?.Data?.Position === sliderValue && targetValue !== sliderValue) return;
      setSliderValue(blindPositionState?.Data?.Position);
      setTargetValue(blindPositionState?.Data?.Position);
    }
  }, [channel.States]);

  const position = blindPositionState?.Data?.Position || 0;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => setTargetValue(Number(event.target.value));

  const handleDown = () => handleAction(BlindActionEnum.Close);

  const handleStop = () => {
    handleAction(BlindActionEnum.Stop);
    setTargetValue(sliderValue);
  };

  const handleUp = () => handleAction(BlindActionEnum.Open);

  return (
    <>
      {status && !calibrationNeed && (
        <EmptyStateBox
          header={td(`exalus.blind.status.${status}.header`)}
          content={td(`exalus.blind.status.${status}.content`)}
          icon={<IconWarning />}
          className="m-b-24"
        />
      )}
      <LavvaDetailsWrapper>
        <div
          className={classNames('cover-detail-view', {
            'cover-detail-view--full-space': isViewSimple || calibrationNeed,
          })}
        >
          {isViewSimple || calibrationNeed ? (
            <ControlWrapper>
              {connectionIcon && <IconConnectionError big size={14} />}
              <div>
                {calibrationNeed && (
                  <EmptyStateBox
                    header={tc('status.calibrate')}
                    content={td(
                      calibrationNeed !== ConfigurationStateEnum.OngoingConfiguration
                        ? 'calibrationInProgressInfo'
                        : 'calibrationNeededSimple',
                    )}
                    icon={<IconWarning />}
                    className="m-b-24"
                  />
                )}
                <BlindDetailsSimpleControlButtons
                  open={open}
                  stop={stop}
                  close={close}
                  remoteButtonState={remoteButtonState}
                  active={active}
                />
              </div>
            </ControlWrapper>
          ) : (
            <>
              <ControlWrapper>
                <div className="cover-detail-view__slider">
                  {connectionIcon && <IconConnectionError big size={14} />}
                  <Slider
                    onPointerUp={handleSliderEvent}
                    value={sliderValue}
                    targetValue={targetValue}
                    onChange={onChange}
                    active={active}
                    error={!!channelOrientedError}
                    showValue={false}
                  />
                </div>
              </ControlWrapper>
              <BlindDetailsControls
                downButton={{
                  handleClick: handleDown,
                }}
                stopButton={{
                  handleClick: handleStop,
                }}
                upButton={{
                  handleClick: handleUp,
                }}
              />
              <Favourites channel={channel} setTargetValue={setTargetValue} position={position} active={active} />
            </>
          )}
        </div>
      </LavvaDetailsWrapper>
    </>
  );
};
